import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTag,
  faBuilding,
  faEnvelope,
  faKaaba,
  faBoxesStacked,
  faComments,
  faTruck,
  faShuffle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImageUrl,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontProducts,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import { absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { listDashboardNewsPosts } from '../lib/news'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

const PRODUCT_NAME = 'Grosir Perlengkapan Haji Umroh'
const PRODUCT_IMAGE_TRANSFORM = { width: 480, format: 'webp', quality: 70 } as const

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Store-owner (kulakan) FAQ: this buyer resells to jamaah and muslim-fashion
// shoppers, so it answers margin, MOQ, mix, and restock questions. The last
// item routes travel agencies to their own page.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Bagaimana cara meminta harga grosir?',
    a: 'Harga mengikuti produk dan jumlah pesanan. Minta quotation, lalu hitung margin setelah seluruh biaya penjualan.',
  },
  {
    q: 'Minimal ordernya berapa?',
    a: 'Minimum order mengikuti kategori produk. Handuk umumnya mulai 10 lusin dan kaos kaki umumnya 50 lusin. Mulai dari kategori yang sesuai dengan pelanggan toko Anda, lalu konfirmasikan MOQ per produk.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Produk apa saja yang tersedia untuk pembelian grosir?',
    a: 'Pilihan produk mencakup mukena, kaos kaki salat muslimah, sarung lengan haji dan umroh, manset lengan cincin, manset tangan, kaos tangan, handuk katun, dan head buff. Kirim nama kategori yang dicari agar admin dapat memeriksa produk yang tersedia.',
  },
  {
    q: 'Apakah warna dan ukuran dapat dicampur?',
    a: 'Ketentuan campur warna dan ukuran berbeda per produk. Kirim pembagian varian yang Anda butuhkan agar admin dapat memeriksanya sebelum quotation dibuat.',
  },
  {
    q: 'Apakah saya dapat memesan sample?',
    a: 'Bisa. Biaya sample dihitung dari harga satuan dan ongkir. Gunakan sample untuk memeriksa bahan, ukuran, jahitan, dan warna sebelum memesan dalam jumlah grosir.',
  },
  {
    q: 'Bagaimana cara pembayarannya?',
    a: 'Pembayaran dilakukan melalui transfer BCA atau Mandiri. Admin memeriksa bukti transfer dan mengonfirmasi pembayaran sebelum pesanan disiapkan.',
  },
  {
    q: 'Pengirimannya berapa lama?',
    a: 'Admin memeriksa waktu penyiapan setelah produk, jumlah, dan pembayaran dikonfirmasi. Estimasi perjalanan mengikuti kota tujuan serta layanan ekspedisi. Nomor resi dikirim setelah paket diserahkan kepada ekspedisi.',
  },
  {
    q: 'Saya baru akan membuka toko oleh-oleh haji. Informasi apa yang perlu saya kirim?',
    a: 'Kirim jenis toko, produk yang dicari, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan produk, MOQ, quotation, serta pengiriman.',
  },
  {
    q: 'Saya mengurus travel dan membutuhkan paket per jamaah. Apakah bisa?',
    a: 'Bisa. Kirim jumlah jamaah, isi paket, kota tujuan, target penerimaan, dan tanggal keberangkatan. Admin akan memeriksa MOQ, stok, waktu penyiapan, serta estimasi perjalanan sebelum menyusun quotation per jamaah.',
    link: { href: '/perlengkapan-haji-umroh', label: 'Lihat halaman perlengkapan jamaah untuk travel →' },
  },
]

export const Route = createFileRoute('/grosir-perlengkapan-haji-umroh')({
  loader: async () => {
    // Config failure falls back to null: the contact helpers carry hardcoded
    // defaults, and paid traffic must never land on a 500.
    const [config, testimonialsRaw, mukena, kaosKaki, sarungLengan, mansetHaji, mansetTangan, handuk, headBuff, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      fetchCatalogProductCached('mukena', 'mukena'),
      fetchCatalogProductCached('kaos kaki solat muslimah', 'kaos-kaki'),
      fetchCatalogProductCached('sarung lengan haji umroh', 'sarung-lengan'),
      fetchCatalogProductCached('manset lengan cincin haji', 'manset-haji'),
      fetchCatalogProductCached('manset tangan', 'manset-tangan'),
      fetchCatalogProductCached('handuk mandi katun', 'handuk'),
      fetchCatalogProductCached('head buff', 'head-buff'),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      products: { mukena, kaosKaki, sarungLengan, mansetHaji, mansetTangan, handuk, headBuff },
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Grosir Perlengkapan Haji & Umroh untuk Toko')
    const description =
      'Grosir perlengkapan haji dan umroh untuk toko oleh-oleh dan busana muslim. Bandingkan kaos kaki salat, sarung lengan, manset, handuk, serta MOQ setiap produk.'

    return {
      // Indexable: targets store owners searching kulakan/grosir intent for
      // haji-umroh items and doubles as a paid-ads destination.
      meta: [
        { title },
        { name: 'description', content: description },
        // Ads-only destination; the matching organic page carries this intent.
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/grosir-perlengkapan-haji-umroh') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/grosir-perlengkapan-haji-umroh') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: StoreLandingPage,
})

// A real storefront product resolved in the loader, so each showcase card
// shows an actual photo and links to its product page.
type CatalogProduct = { name: string; slug: string; image: string | null }

// dropifyFetch deliberately never caches /storefront/products, but these
// showcase lookups don't need per-request freshness. Cache them at the route
// level so ad clicks don't pay six API round-trips of TTFB.
const CATALOG_PRODUCT_TTL_MS = 60 * 60 * 1000
const CATALOG_PRODUCT_MISS_TTL_MS = 5 * 60 * 1000
const catalogProductCache = new Map<string, { expiresAt: number; product: CatalogProduct | null }>()

async function fetchCatalogProductCached(search: string, cacheKey: string): Promise<CatalogProduct | null> {
  const cached = catalogProductCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) return cached.product
  let product: CatalogProduct | null = null
  try {
    const response = await listStorefrontProducts({ search, per_page: 3 })
    const first = (response.items ?? [])[0]
    if (first) {
      const url = getProductPrimaryImageUrl(first)
      product = {
        name: first.name,
        slug: first.slug || first.id,
        image: url ? toExternalImage(url, PRODUCT_IMAGE_TRANSFORM) : null,
      }
    }
  } catch {
    product = null
  }
  const ttlMs = product ? CATALOG_PRODUCT_TTL_MS : CATALOG_PRODUCT_MISS_TTL_MS
  catalogProductCache.set(cacheKey, { expiresAt: Date.now() + ttlMs, product })
  return product
}

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sebelum pembayaran',
    desc: 'Admin merinci produk, jumlah, harga, dan ongkir melalui quotation sebelum Anda membayar.',
  },
  {
    icon: faBoxesStacked,
    title: 'Beberapa kategori dalam satu daftar',
    desc: 'Mukena, kaos kaki, sarung lengan, manset, handuk, dan head buff dapat dicantumkan dalam satu permintaan quotation.',
  },
  {
    icon: faShuffle,
    title: 'Ketentuan campur varian',
    desc: 'Ketentuan campur warna dan ukuran diperiksa per produk sebelum susunan pesanan disetujui.',
  },
  {
    icon: faComments,
    title: 'Restock melalui WhatsApp',
    desc: 'Kirim kembali kode produk dan jumlah. Admin akan memeriksa stok serta membuat quotation terbaru.',
  },
]

const FIT_ITEMS = [
  'Toko oleh-oleh haji dan umroh yang membeli produk untuk dijual kembali.',
  'Toko busana muslim online atau offline yang ingin menambah perlengkapan haji dan umroh.',
  'Toko perlengkapan ibadah yang mencari kaos kaki salat, manset, sarung lengan, atau handuk.',
  'Reseller busana muslim yang menghitung margin dari quotation dan seluruh biaya penjualan.',
  'Pemilik toko baru yang sedang menentukan kategori, jumlah, dan modal awal.',
  'Pembeli yang perlu memeriksa stok, harga, dan waktu penyiapan sebelum restock.',
]

const OBJECTIONS = [
  {
    worry: 'Produk apa yang perlu dipilih lebih dahulu?',
    answer: 'Mulai dari produk yang sudah ditanyakan pelanggan. Catat model, warna, dan ukuran yang terjual sebagai dasar restock berikutnya.',
  },
  {
    worry: 'Bagaimana menghitung modal awal?',
    answer: 'MOQ dihitung per kategori. Handuk umumnya mulai 10 lusin. Konfirmasikan satuan dan ketentuan campur varian sebelum menentukan modal.',
  },
  {
    worry: 'Bagaimana menghitung margin?',
    answer: 'Gunakan quotation terbaru dan hitung harga jual setelah memasukkan ongkir, kemasan, biaya platform, promosi, dan retur.',
  },
  {
    worry: 'Bagaimana merencanakan restock untuk periode keberangkatan?',
    answer: 'Kirim target penerimaan dan perkiraan jumlah sejak awal. Admin akan memeriksa ketersediaan serta waktu penyiapan sebelum Anda memesan.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function StoreLandingPage() {
  const { config, testimonialImages, products, newsPosts } = Route.useLoaderData()

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya mau kulakan perlengkapan haji dan umroh untuk toko. Mohon kirim katalog, MOQ, dan cara meminta quotation.'
  const softMessage = 'Halo Dropify, saya sedang membandingkan perlengkapan haji dan umroh. Mohon jelaskan pilihan produk dan ketentuan order.'
  const ctaMessage = 'Halo Dropify, saya mau mulai menjual perlengkapan haji dan umroh. Mohon kirim katalog, MOQ, dan cara order.'

  // Showcase cards from real storefront products; a card only renders when its
  // product resolves, so the section never shows placeholder imagery.
  const showcaseItems = [
    products.mukena && {
      product: products.mukena,
      title: 'Mukena',
      desc: 'Kategori mukena mencakup model renda dan terusan. Periksa bahan, ukuran, detail hiasan, serta warna per model.',
      tag: 'Mukena',
    },
    products.kaosKaki && {
      product: products.kaosKaki,
      title: 'Kaos Kaki Solat Muslimah',
      desc: 'Kaos kaki salat muslimah tersedia dalam beberapa model. Periksa ukuran, bahan, dan konstruksi anti-slip per produk.',
      tag: 'Kaos kaki salat',
    },
    products.sarungLengan && {
      product: products.sarungLengan,
      title: 'Sarung Lengan Haji & Umroh',
      desc: 'Sarung lengan tersedia sebagai pelengkap pakaian jamaah. Periksa bahan, panjang, dan ukuran sebelum memilih.',
      tag: 'Penutup lengan',
    },
    products.mansetHaji && {
      product: products.mansetHaji,
      title: 'Manset Lengan Cincin Haji',
      desc: 'Manset dengan cincin. Periksa bahan, ukuran, dan kekencangan karetnya sebelum memasukkannya ke katalog toko.',
      tag: 'Model cincin',
    },
    products.mansetTangan && {
      product: products.mansetTangan,
      title: 'Manset Tangan',
      desc: 'Manset tangan tersedia dalam pilihan warna. Konfirmasikan ketentuan campur warna untuk produk yang dipilih.',
      tag: 'Manset tangan',
    },
    products.handuk && {
      product: products.handuk,
      title: 'Handuk Katun',
      desc: 'Handuk katun untuk paket perjalanan atau stok toko. Periksa ukuran, gramasi, warna, dan isi kemasan setiap model.',
      tag: 'Pelengkap paket',
    },
    products.headBuff && {
      product: products.headBuff,
      title: 'Head Buff',
      desc: 'Head buff tersedia sebagai penutup kepala dan leher. Periksa bahan, ukuran, motif, dan isi kemasannya.',
      tag: 'Penutup kepala',
    },
  ].filter(Boolean) as Array<{ product: CatalogProduct; title: string; desc: string; tag: string }>

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Kirim target penerimaan jika pesanan disiapkan untuk musim umroh atau haji.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta katalog dan MOQ"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + best-seller list card, over a Kaaba photo backdrop.
          The image is anchored to its bottom (the Kaaba, not the poster text
          baked into its top half) and sits under a heavy navy overlay so the
          copy stays readable. If the file is missing the hero degrades to the
          plain dark background. */}
      <section className="relative bg-[#0B1120] overflow-hidden">
        <div aria-hidden className="absolute inset-0">
          <img
            src="/lp/haji-umroh-hero.jpg"
            alt=""
            width="1086"
            height="1448"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-bottom opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/90 via-[#0B1120]/75 to-[#0B1120]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faKaaba} className="h-3 w-3" />
              Untuk toko oleh-oleh haji dan busana muslim
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Pilih perlengkapan haji dan umroh <Mark>berdasarkan pelanggan toko Anda.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Dropify menyediakan kaos kaki salat, sarung lengan haji, manset, handuk, dan head buff untuk pembelian grosir. Ketentuan campur varian, stok, dan harga dikonfirmasi melalui WhatsApp.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta katalog dan quotation"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Tanyakan pilihan produk →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Sample dapat dipesan dengan harga satuan dan ongkir sebelum pembelian grosir.</p>
          </div>

          {/* Hero visual: best-seller checklist */}
          <div className="relative">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-300">Pilihan untuk toko haji dan busana muslim</p>
              <ul className="mt-4 space-y-3">
                {[
                  'Mukena dengan pilihan model dan ukuran',
                  'Kaos kaki salat muslimah',
                  'Sarung lengan haji & umroh panjang',
                  'Manset lengan cincin & manset tangan',
                  'Handuk katun untuk paket oleh-oleh',
                  'Head buff untuk penutup kepala dan leher',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-xl bg-white/5 px-4 py-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                      <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />
                    </span>
                    <p className="text-base leading-relaxed text-slate-200">{item}</p>
                  </li>
                ))}
              </ul>
              <p className="mt-5 border-t border-white/10 pt-4 text-sm text-slate-400">
                Susun beberapa kategori dalam satu daftar kebutuhan.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Brand-protection notice — Dropify only takes orders via WhatsApp */}
      <section className="bg-white pt-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <p className="text-base leading-relaxed text-slate-700">
              <span className="font-bold text-slate-900">Pemesanan resmi Dropify dilakukan melalui WhatsApp.</span> Dropify tidak membuka toko di Tokopedia, Shopee, Lazada, atau marketplace lain. Pastikan nomor tujuan sesuai dengan kontak yang tercantum di website ini.
            </p>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pembeli yang dilayani halaman ini</h2>
            <p className="mt-2 text-base text-slate-500">Informasi berikut ditujukan untuk toko dan reseller yang membeli perlengkapan haji serta umroh secara grosir.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Informasi yang tersedia sebelum Anda memesan</h2>
            <p className="mt-2 text-base text-slate-500">Periksa rincian quotation, ketentuan campur varian, dan proses restock melalui WhatsApp.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product showcase from real storefront products */}
      {showcaseItems.length > 0 && (
        <section className="py-14 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="mb-8 max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Bandingkan produk untuk stok toko</h2>
              <p className="mt-2 text-base text-slate-500">Bandingkan fungsi, bahan, ukuran, dan MOQ setiap produk sebelum menyusun pesanan.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {showcaseItems.map((item) => (
                <div key={item.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                  {item.product.image && (
                    <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                      <img src={item.product.image} alt={item.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                      <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{item.tag}</span>
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                    <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{item.desc}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <WhatsAppButton
                        message={`Halo Dropify, saya tertarik kulakan ${item.title}. Mohon cek MOQ, informasi stok, dan quotation untuk jumlah ___.`}
                        label="Periksa stok dan harga"
                        itemName={item.title}
                        className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                      />
                      <a href={`/produk/${item.product.slug}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                        Lihat detail produk →
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: faBoxesStacked, text: 'Stok dan waktu penyiapan dikonfirmasi sebelum order' },
                { icon: faShuffle, text: 'Ketentuan campur warna dan ukuran diperiksa per produk' },
                { icon: faTruck, text: 'Pengiriman antarkota disertai nomor resi' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      {/* Objection handling */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Hal yang perlu dihitung sebelum kulakan</h2>
            <p className="mt-2 text-base text-slate-500">Periksa harga, MOQ, campur varian, sample, pembayaran, dan rencana restock.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim pertanyaan Anda kepada admin sebelum menentukan pesanan.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan kepada admin"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Grosir perlengkapan haji dan umroh untuk stok toko</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify melayani <strong className="font-semibold text-slate-800">pembelian grosir perlengkapan haji</strong> untuk toko oleh-oleh, toko busana muslim, dan toko perlengkapan ibadah. Pesanan diproses melalui WhatsApp berdasarkan produk, jumlah, varian, dan kota tujuan.
            </p>
            <p>
              Pilihan kategori mencakup mukena, kaos kaki salat muslimah, sarung lengan haji dan umroh, manset lengan cincin, manset tangan, handuk, serta head buff. Ketentuan campur warna dan ukuran berbeda per produk. Kirim target penerimaan jika pesanan disiapkan untuk periode keberangkatan tertentu.
            </p>
            <p>
              Halaman ini ditujukan untuk kulakan toko. Pengurus travel yang membutuhkan paket per jamaah dapat membuka halaman{' '}
              <a href="/perlengkapan-haji-umroh" className="font-semibold text-blue-600 hover:text-blue-700">
                perlengkapan jamaah untuk travel
              </a>{' '}
              kami.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation untuk stok toko</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Kirim daftar produk, jumlah, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation sebelum Anda memesan.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation melalui WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Pastikan model, varian, satuan, dan jumlah pada quotation sudah sesuai.
          </p>

          {/* Legitimacy: accepted payment methods */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/grosir-perlengkapan-haji-umroh" />

      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pemesanan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan grosir yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar kulakan perlengkapan haji</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'lp' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
